import { CONTENT_FITNESS_ASSESSMENT_MODE } from "../../../enums/content-fitness";
import { CONTENT_FITNESS_FRAMEWORK_VERSION } from "../version";
import { anchors, type ContentFitnessSubcriterion, deferred } from "./types";

export const CONTENT_DESIGN_ORIENTATION_SUBCRITERIA = [
  {
    key: "content_design_orientation.heading_hierarchy",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Heading hierarchy",
    parentMetricKey: "content_design_orientation",
    order: 1,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    pilot: true,
    definition:
      "Scores whether headings create a clear page outline with one descriptive H1, logical nesting and meaningful section labels.",
    evidenceGuidance:
      "Use H1/H2/H3 sequence, page outline, heading text, visible headings and DOM headings. Do not infer semantic structure from visual style alone.",
    applicabilityNotes:
      "Very small pages may have fewer headings; judge against content complexity.",
    anchors: anchors(
      "No meaningful heading structure, or headings actively misrepresent the page.",
      "Major outline problems such as missing/duplicated H1s, empty headings or confusing level jumps dominate.",
      "Basic heading structure exists, but notable nesting, specificity or section-label gaps weaken orientation.",
      "Clear heading outline with one descriptive H1 and mostly logical H2/H3 structure; minor local issues only.",
      "Headings form an accurate, complete and highly usable outline for both readers and parsers.",
    ),
    objectiveChecks: ["H1 count", "skipped heading levels", "empty headings"],
  },
  deferred(
    "content_design_orientation.metadata_titles",
    "Metadata & titles",
    "content_design_orientation",
    2,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether the title and meta description are present, unique, accurate and useful as page orientation.",
  ),
  {
    key: "content_design_orientation.structured_data",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Structured data",
    parentMetricKey: "content_design_orientation",
    order: 3,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.OBJECTIVE,
    pilot: true,
    definition:
      "Scores whether relevant structured data is present and valid where it would help the page's discoverability and orientation.",
    evidenceGuidance:
      "Use JSON-LD, microdata, schema types, validation errors and page type. Distinguish design support from AI meaning.",
    applicabilityNotes:
      "If the page type has no meaningful schema need, mark not_applicable with rationale rather than penalising.",
    anchors: anchors(
      "Absent where clearly required or invalid enough to be unusable.",
      "Malformed, missing key fields or wrong type.",
      "Partial schema of limited relevance or schema with material warnings.",
      "Valid schema with minor gaps.",
      "Relevant valid schema with useful required fields.",
    ),
    objectiveChecks: [
      "schema present where applicable",
      "schema parses",
      "relevant type used",
      "required/recommended properties populated",
      "no validation errors",
    ],
  },
  deferred(
    "content_design_orientation.internal_linking_depth",
    "Internal linking & depth",
    "content_design_orientation",
    4,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether the page is placed in the estate through sensible internal links, reasonable depth and sitemap inclusion.",
  ),
  deferred(
    "content_design_orientation.machine_readability",
    "Machine readability",
    "content_design_orientation",
    5,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether key page content is extractable as clean text and basic machine-readable structure.",
  ),
  deferred(
    "content_design_orientation.semantic_structure_markup",
    "Semantic structure & markup",
    "content_design_orientation",
    6,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether the DOM uses semantic HTML and landmarks so structure is explicit, not just visual.",
  ),
  deferred(
    "content_design_orientation.content_order_chunking",
    "Content order & chunking",
    "content_design_orientation",
    7,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether information is broken into self-contained, clearly labelled sections with a logical reading order.",
  ),
  deferred(
    "content_design_orientation.url_design",
    "URL design",
    "content_design_orientation",
    8,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether URLs are descriptive, human-readable and reflect the page's place in the site hierarchy.",
  ),
] as const satisfies readonly ContentFitnessSubcriterion[];
